import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { TelegramSettings } from '@/components/TelegramSettings';
import { isPremiumTier } from '@/lib/subscription';

export default function Account() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: 'Success',
        description: 'You have been logged out.',
      });
      setLocation('/');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to logout',
        variant: 'destructive',
      });
    }
  };

  const handleUpgradeClick = () => {
    setLocation('/subscribe');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 pt-24">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const memberSince = new Date(user.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const subscriptionTierColor = isPremiumTier(user.subscriptionTier)
    ? 'bg-primary text-primary-foreground'
    : 'bg-secondary text-secondary-foreground';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-10">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Profile Card */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-lg font-semibold">{user.email}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Account ID</p>
                <p className="text-sm font-mono text-muted-foreground">{user.id}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Member Since</p>
                <p className="text-lg">{memberSince}</p>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Card */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
              <CardDescription>Manage your subscription plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Current Plan</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge className={subscriptionTierColor}>
                      {isPremiumTier(user.subscriptionTier) ? 'Premium' : 'Free'}
                    </Badge>
                    {user.subscriptionStatus && (
                      <span className="text-sm text-muted-foreground">
                        Status: {user.subscriptionStatus}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {!isPremiumTier(user.subscriptionTier) && (
                <div className="pt-2">
                  <Button onClick={handleUpgradeClick} className="w-full">Upgrade to Pro</Button>
                </div>
              )}

              {isPremiumTier(user.subscriptionTier) && (
                <div className="pt-2">
                  <Button variant="outline" className="w-full" onClick={handleUpgradeClick}>
                    Manage Subscription
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Telegram Settings */}
          <TelegramSettings />

          <Separator />

          {/* Logout Section */}
          <div className="flex justify-center pt-4">
            <Button variant="destructive" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
